"use client";

import { useState } from "react";
import { useTranslation } from "../../../i18n";
import {
  Play,
  Pause,
  RotateCcw,
  ChevronRight,
  CheckCircle,
  Clipboard,
  Settings,
  Rocket,
  BarChart3,
  type LucideIcon,
} from "lucide-react";

interface LifecycleStep {
  id: number;
  title: string;
  description: string;
  details: string;
  icon: LucideIcon;
  color: string;
  actions: string[];
}

export default function DigitalTwinsLifecycle() {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);

  const lifecycleSteps: LifecycleStep[] = [
    {
      id: 1,
      title: t('routes.digitalTwins.lifecycle.steps.0.title'),
      description: t('routes.digitalTwins.lifecycle.steps.0.description'),
      details: t('routes.digitalTwins.lifecycle.steps.0.details'),
      icon: Clipboard,
      color: "bg-blue-500",
      actions: [
        t('routes.digitalTwins.lifecycle.steps.0.actions.0'),
        t('routes.digitalTwins.lifecycle.steps.0.actions.1'),
        t('routes.digitalTwins.lifecycle.steps.0.actions.2'),
      ],
    },
    {
      id: 2,
      title: t('routes.digitalTwins.lifecycle.steps.1.title'),
      description: t('routes.digitalTwins.lifecycle.steps.1.description'),
      details: t('routes.digitalTwins.lifecycle.steps.1.details'),
      icon: Settings,
      color: "bg-purple-500",
      actions: [
        t('routes.digitalTwins.lifecycle.steps.1.actions.0'),
        t('routes.digitalTwins.lifecycle.steps.1.actions.1'),
        t('routes.digitalTwins.lifecycle.steps.1.actions.2'),
        t('routes.digitalTwins.lifecycle.steps.1.actions.3'),
      ],
    },
    {
      id: 3,
      title: t('routes.digitalTwins.lifecycle.steps.2.title'),
      description: t('routes.digitalTwins.lifecycle.steps.2.description'),
      details: t('routes.digitalTwins.lifecycle.steps.2.details'),
      icon: Rocket,
      color: "bg-green-500",
      actions: [
        t('routes.digitalTwins.lifecycle.steps.2.actions.0'),
        t('routes.digitalTwins.lifecycle.steps.2.actions.1'),
        t('routes.digitalTwins.lifecycle.steps.2.actions.2'),
        t('routes.digitalTwins.lifecycle.steps.2.actions.3'),
      ],
    },
    {
      id: 4,
      title: t('routes.digitalTwins.lifecycle.steps.3.title'),
      description: t('routes.digitalTwins.lifecycle.steps.3.description'),
      details: t('routes.digitalTwins.lifecycle.steps.3.details'),
      icon: RotateCcw,
      color: "bg-amber-500",
      actions: [
        t('routes.digitalTwins.lifecycle.steps.3.actions.0'),
        t('routes.digitalTwins.lifecycle.steps.3.actions.1'),
        t('routes.digitalTwins.lifecycle.steps.3.actions.2'),
        t('routes.digitalTwins.lifecycle.steps.3.actions.3'),
      ],
    },
    {
      id: 5,
      title: t('routes.digitalTwins.lifecycle.steps.4.title'),
      description: t('routes.digitalTwins.lifecycle.steps.4.description'),
      details: t('routes.digitalTwins.lifecycle.steps.4.details'),
      icon: BarChart3,
      color: "bg-indigo-500",
      actions: [
        t('routes.digitalTwins.lifecycle.steps.4.actions.0'),
        t('routes.digitalTwins.lifecycle.steps.4.actions.1'),
        t('routes.digitalTwins.lifecycle.steps.4.actions.2'),
        t('routes.digitalTwins.lifecycle.steps.4.actions.3'),
      ],
    },
  ];

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    if (!isPlaying) {
      const interval = setInterval(() => {
        setCurrentStep((prev) => {
          if (prev >= lifecycleSteps.length) {
            setIsPlaying(false);
            clearInterval(interval);
            return 1;
          }
          return prev + 1;
        });
      }, 3000);
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
    setIsPlaying(false);
  };

  const activeStep = lifecycleSteps[currentStep - 1];

  return (
    <section className="py-16 bg-gradient-to-br from-purple-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('routes.digitalTwins.lifecycle.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {t('routes.digitalTwins.lifecycle.description')}
          </p>

          {/* Controls */}
          <div className="flex justify-center space-x-4 mb-8">
            <button
              onClick={handlePlayPause}
              className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              {isPlaying ? (
                <Pause className="h-5 w-5 mr-2" />
              ) : (
                <Play className="h-5 w-5 mr-2" />
              )}
              {isPlaying ? t('routes.digitalTwins.lifecycle.pause') : t('routes.digitalTwins.lifecycle.play')} Lifecycle
            </button>
            <button
              onClick={handleReset}
              className="inline-flex items-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <RotateCcw className="h-5 w-5 mr-2" />
              {t('routes.digitalTwins.lifecycle.reset')}
            </button>
          </div>
        </div>

        {/* Lifecycle Visualization */}
        <div className="relative mb-12">
          {/* Progress Line */}
          <div className="absolute top-16 left-0 right-0 h-1 bg-gray-200 rounded-full">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-1000"
              style={{
                width: `${(currentStep / lifecycleSteps.length) * 100}%`,
              }}
            />
          </div>

          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 relative">
            {lifecycleSteps.map((step, index) => (
              <div key={step.id} className="relative">
                {/* Step Circle */}
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl mb-4 mx-auto transition-all duration-500 cursor-pointer ${
                    currentStep >= step.id
                      ? `${step.color} text-white shadow-lg scale-110`
                      : "bg-gray-200 text-gray-400 hover:bg-gray-300"
                  }`}
                  onClick={() => setCurrentStep(step.id)}
                >
                  <step.icon className="w-6 h-6" />
                </div>

                {/* Arrow */}
                {index < lifecycleSteps.length - 1 && (
                  <div className="hidden md:block absolute top-8 -right-4 transform -translate-y-1/2">
                    <ChevronRight
                      className={`h-6 w-6 transition-colors ${
                        currentStep > step.id
                          ? "text-purple-500"
                          : "text-gray-300"
                      }`}
                    />
                  </div>
                )}

                {/* Step Content */}
                <div
                  className={`text-center transition-all duration-500 ${
                    currentStep >= step.id
                      ? "opacity-100 transform translate-y-0"
                      : "opacity-50 transform translate-y-2"
                  }`}
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Active Step Details */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className={`${activeStep.color} p-6 text-white`}>
            <div className="flex items-center mb-4">
              <div className="text-4xl mr-4">
                <activeStep.icon className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">
                  {t('routes.digitalTwins.lifecycle.step')} {currentStep}: {activeStep.title}
                </h3>
                <p className="text-white/90 mt-1">{activeStep.details}</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              {t('routes.digitalTwins.lifecycle.keyActions')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {activeStep.actions.map((action, index) => (
                <div key={index} className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">{action}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
